import { useEffect, useState } from 'react';
import * as brazil from 'cpf-cnpj-validator';

import AmountInput from 'components/ui/Input/AmountInput';
import Input from 'components/ui/Input/Base';
import { DefaultInputType } from 'components/ui/Input/Base/types';
import { DEFAULT_INPUT_VALUE } from 'components/ui/Input/Base/constant';
import { Selector } from 'react-gldn-kit';

import { setFormsInfoAction } from 'stores/money/forms/selectors';
import { useZustandFormsStore } from 'stores/money/forms/store';
import { setPaymentsCurrentStepAction } from 'stores/money/payments/selectors';
import { useZustandPaymentsStore } from 'stores/money/payments/store';
import { getUserCurrencyIdSelector } from 'stores/user/personal/selectors';
import { useZustandUserStore } from 'stores/user/personal/store';
import { Steps } from 'stores/money/payments/types';

import {
  composeValidations,
  isFilledForm,
  isRequired,
  validateEmailValue,
} from 'utils/validation';

import { ERequestIds } from 'react-memory-optimization/dist/lib/binary/types';

import { PaymentChannelType } from 'react-memory-optimization/dist/lib/store/user/types';

import PixAccountInput from 'components/ui/Input/PixInput';

import InputUserInfo from 'components/ui/Input/InputUserInfo';
import styles from '../../Withdraw.module.css';
import PhoneInput from 'components/ui/Input/Phone';
import { BANKS } from './constants';
import PortalList from '../../../PortalList';

type Props = {
  min: number;
  max: number;
  channelId:
    | PaymentChannelType.PlixopayPayco
    | PaymentChannelType.PlixopayFast
    | PaymentChannelType.PlixopayPaybol
    | PaymentChannelType.PlixopayPaypay
    | PaymentChannelType.PlixopayPopypara
    | PaymentChannelType.PlixopayParaparola
    | PaymentChannelType.PlixopayPapara;
};

const TryBasePlixoForm = (props: Props) => {
  const { min, max, channelId } = props;

  const setFormInfo = useZustandFormsStore(setFormsInfoAction);

  const setStep = useZustandPaymentsStore(setPaymentsCurrentStepAction);

  const currencyId = useZustandUserStore(getUserCurrencyIdSelector);

  const [amount, setAmount] = useState('');

  const [name, setName] = useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const [isOpenPortal, setOpenPortal] = useState(false);

  const [sureName, setSurname] =
    useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const [bankInfo, setBankInfo] = useState<null | { id: number; name: string }>(
    null
  );
  const [email, setEmail] = useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const [phone, setPhone] = useState<string>('');

  const [documentId, setDocumentId] =
    useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const handleTogglePortal = () => {
    setOpenPortal(!isOpenPortal);
  };

  const handleSaveBankCode =
    (b: any) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleTogglePortal();
      setBankInfo(b);
    };

  const handleChangeInput = (type: 'documentId') => (value: string) => {
    if (type === 'documentId') {
      const errorText = composeValidations([
        isRequired({ value, errorText: 'errors.required' }),
      ]);

      setDocumentId({
        value,
        errorText,
      });
    }
  };

  useEffect(() => {
    const data = [amount, name, sureName];

    if (channelId === PaymentChannelType.PlixopayFast) {
      data.push(`${bankInfo?.id || ''}`);
    }

    if (
      channelId !== PaymentChannelType.PlixopayFast &&
      channelId !== PaymentChannelType.PlixopayPayco
    ) {
      data.push(...[phone, email, documentId]);
    }
    const isFilled = isFilledForm(data);

    if (currencyId && isFilled && bankInfo) {
      const bankId =
        `${bankInfo?.id}`.length === 2
          ? `000${bankInfo.id}`
          : `00${bankInfo.id}`;

      setFormInfo({
        isDeposit: false,
        form: {
          currencyId: currencyId,
          channelId,
          name: name.value,
          sureName: sureName.value,
          email: email.value,
          phone: phone.slice(3),
          documentId: documentId.value,
          bankName: bankId,
          amount,
          requestId: ERequestIds.WithdrawRequest,
        },
      });

      setStep(Steps.FilledForm);
    } else {
      setStep(Steps.Amount);
      setFormInfo(null);
    }
  }, [
    amount,
    name,
    sureName,
    currencyId,
    channelId,
    email,
    phone,
    documentId,
    bankInfo,
  ]);

  // Two - 000
  //  three 00 5 digit

  return (
    <div>
      <AmountInput
        isWithdraw
        placeholder="walletPopUp.forms.amount"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        size="full"
        onChange={setAmount}
        isPresets={false}
        min={min}
        max={max}
        wrapperClasses={styles.marginInputBottom}
      />
      <InputUserInfo
        userInfoKey="name"
        isIgnoreUserInfo
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.name"
        size="full"
        classes={styles.marginInputBottom}
        onChange={setName}
        field={name}
      />

      <InputUserInfo
        userInfoKey="surname"
        isIgnoreUserInfo
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.sureName"
        size="full"
        field={sureName}
        onChange={setSurname}
        classes={styles.marginInputBottom}
      />

      {channelId !== PaymentChannelType.PlixopayFast &&
        channelId !== PaymentChannelType.PlixopayPayco && (
          <>
            <PhoneInput
              countryCodeDefault="TR"
              withConfirm={false}
              size="full"
              inputStyleType="border-colorful"
              inputLabelType="labelUp"
              sendKey="09x"
              onChange={setPhone}
              isHidePlaceholder={false}
              baseValue="+91"
              placeholder="walletPopUp.forms.phone"
              classesWrapper={styles.marginInputBottom}
            />
            <InputUserInfo
              size="full"
              userInfoKey="email"
              inputStyleType="border-colorful"
              inputLabelType="labelUp"
              validationFn={validateEmailValue}
              onChange={setEmail}
              field={email}
              placeholder="walletPopUp.forms.email"
              classes={styles.marginInputBottom}
            />
            <Input
              placeholder="walletPopUp.forms.documentId"
              inputStyleType="border-colorful"
              inputLabelType="labelUp"
              size="full"
              classes={styles.marginInputBottom}
              errorText={documentId.errorText}
              onChange={handleChangeInput('documentId')}
            />
          </>
        )}

      {channelId === PaymentChannelType.PlixopayFast && (
        <Selector
          placeholder="walletPopUp.forms.bank"
          activeId={bankInfo?.id || -1}
          selectorStyleType="labelUp"
          handleClick={handleTogglePortal}
          data={BANKS}
          classes={styles.marginInputBottom}
          type="button"
          isOpenUp
          size="full"
        />

        // <Input
        //   placeholder="walletPopUp.forms.bankName"
        //   inputStyleType="border-colorful"
        //   inputLabelType="labelUp"
        //   size="full"
        //   classes={styles.marginInputBottom}
        //   errorText={bankName.errorText}
        //   onChange={handleChangeInput('bankName')}
        // />
      )}

      <PortalList
        isOpen={isOpenPortal}
        data={BANKS}
        activeName={bankInfo?.name}
        callback={handleSaveBankCode}
      />
    </div>
  );
};

export default TryBasePlixoForm;
